/**
 * Celine Dion - Plénitude Arena (formerly La Défense Arena) interactive seating map.
 *
 * - Loads the SVG inline (via fetch) so we can attach event handlers
 * - Tooltips on hover showing section + category + from-price + listing count
 * - Clicking a section highlights the matching ticket variant card
 *   and dims the others
 * - Clicking the same section again (or "Show all") clears the filter
 *
 * Phase 1 UX (Aug 2026):
 * - Mobile: cards grouped into per-level accordions + sticky summary bar
 * - Quantity filter chips (Any / 2 / 3 / 4+) above the listings
 * - "All-in pricing" messaging at the point of price
 * - Uniform "from $X" pills on every purchasable map section,
 *   greyed-out sections with zero availability
 * - Bidirectional hover: card <-> map section highlight
 * - Sort control (price asc default / desc), actually reorders cards
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------- i18n --
  function _lang() {
    var l = (document.documentElement.getAttribute('lang') || 'en').toLowerCase();
    if (l.indexOf('fr') === 0) return 'fr';
    if (l.indexOf('es') === 0) return 'es';
    return 'en';
  }

  var STR = {
    en: {
      allin: 'All-in pricing — the price you see is the price you pay. No fees at checkout.',
      allinShort: 'All-in · no fees at checkout',
      qtyLabel: 'How many tickets?',
      any: 'Any',
      sortAsc: 'Price: low to high',
      sortDesc: 'Price: high to low',
      from: 'From',
      listing: 'listing',
      listings: 'listings',
      view: 'View tickets',
      soldout: 'Sold out',
      left: ' left',
      noMatch: 'No tickets match this quantity — tap "Any" to see all listings.'
    },
    fr: {
      allin: 'Prix tout compris — aucuns frais au paiement.',
      allinShort: 'Tout compris · sans frais',
      qtyLabel: 'Combien de billets ?',
      any: 'Tous',
      sortAsc: 'Prix : croissant',
      sortDesc: 'Prix : décroissant',
      from: 'Dès',
      listing: 'offre',
      listings: 'offres',
      view: 'Voir les billets',
      soldout: 'Épuisé',
      left: ' dispo',
      noMatch: 'Aucun billet pour cette quantité — appuyez sur « Tous ».'
    },
    es: {
      allin: 'Precio final — sin cargos ocultos al pagar.',
      allinShort: 'Precio final · sin cargos',
      qtyLabel: '¿Cuántas entradas?',
      any: 'Todas',
      sortAsc: 'Precio: menor a mayor',
      sortDesc: 'Precio: mayor a menor',
      from: 'Desde',
      listing: 'opción',
      listings: 'opciones',
      view: 'Ver entradas',
      soldout: 'Agotado',
      left: ' disp.',
      noMatch: 'Ninguna entrada para esa cantidad — toca "Todas".'
    }
  };
  function T() { return STR[_lang()]; }

  var GROUP_LABELS = {
    en: { 'floor': 'Floor / Parterre', '100-level': '100 Level', 'salon': 'Salon', '400-level': '400 Level' },
    fr: { 'floor': 'Parterre', '100-level': 'Niveau 100', 'salon': 'Salon', '400-level': 'Niveau 400' },
    es: { 'floor': 'Pista', '100-level': 'Nivel 100', 'salon': 'Salon', '400-level': 'Nivel 400' }
  };
  var CAT_ORDER = ['floor', '100-level', 'salon', '400-level'];

  // SVG section data-section prefix → ticket card data-category value
  // (the ticket-variants-table snippet renders cards with
  //  data-category="{{ category_label | handleize }}")
  function _categoryFromSection(sectionId) {
    if (!sectionId) return null;
    if (sectionId.indexOf('floor_') === 0) return 'floor';
    if (sectionId.indexOf('lower_1') === 0) return '100-level';
    if (sectionId.indexOf('lower_2') === 0) return 'salon';
    if (sectionId.indexOf('upper_') === 0) return '400-level';
    return null;
  }

  // Extract the block identifier from a data-section value
  //   "floor_b"    → "b"   (single floor letter)
  //   "lower_116"  → "116" (lower bowl section number)
  //   "lower_216"  → "216" (salon section number)
  //   "upper_414"  → "414" (upper bowl section number)
  function _blockFromSection(sectionId) {
    if (!sectionId) return null;
    var parts = sectionId.split('_');
    if (parts.length < 2) return null;
    return parts.slice(1).join('_').toLowerCase();
  }

  var CATEGORY_LABELS = {
    'floor':     'Floor / Parterre',
    '100-level': '100 Level',
    'salon':     'Salon',
    '400-level': '400 Level / Niveau 400'
  };

  // ------------------------------------------------------- card helpers --
  function allCards() {
    return Array.prototype.slice.call(
      document.querySelectorAll('.cd-map-tickets .ticket-variant-card')
    );
  }
  // Cards not hidden by the quantity filter
  function visibleCards() {
    return allCards().filter(function (c) {
      return !c.classList.contains('cd-qty-hidden');
    });
  }
  function cardPrice(c) {
    return parseFloat(c.getAttribute('data-price'));
  }
  // Max purchasable quantity for a card. The qty input may have been
  // replaced by a <select> (ticket-variants-table.js) that keeps the class
  // but drops the max attribute — fall back to the largest option value.
  function cardMaxQty(c) {
    var q = c.querySelector('.qty-input, .qty-select, select.qty-dropdown');
    if (!q) return 99;
    var m = parseInt(q.getAttribute('max'), 10);
    if (!isNaN(m) && m > 0) return m;
    if (q.tagName === 'SELECT') {
      var best = 0;
      for (var i = 0; i < q.options.length; i++) {
        var v = parseInt(q.options[i].value, 10);
        if (!isNaN(v) && v > best) best = v;
      }
      if (best > 0) return best;
    }
    m = parseInt(q.getAttribute('data-max'), 10);
    return isNaN(m) ? 99 : m;
  }

  // Strip ".00" from product card prices ("$3,000.00 USD" → "$3,000")
  // and kill any currency-code suffix spans. Runs once on init.
  function cleanProductCardPrices() {
    var priceEls = document.querySelectorAll('.product-card .price, .product-card .product-card__price, .product-card .price-item, .product-card sale-price, .product-card compare-at-price');
    Array.prototype.forEach.call(priceEls, function (el) {
      if (el.dataset.cdCleaned) return;
      // Strip ".00" from text nodes recursively
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      var node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue && /\.00\b/.test(node.nodeValue)) {
          node.nodeValue = node.nodeValue.replace(/\.00(?!\d)/g, '');
        }
      }
      el.dataset.cdCleaned = '1';
    });
  }

  function init() {
    cleanProductCardPrices();

    var wrapper = document.querySelector('.cd-map-svg-container');
    if (!wrapper) return;

    var svgUrl = wrapper.getAttribute('data-svg-url');
    if (!svgUrl) return;

    fetch(svgUrl)
      .then(function (r) { return r.text(); })
      .then(function (svgText) {
        wrapper.innerHTML = svgText;
        var svg = wrapper.querySelector('svg');
        if (!svg) return;

        // Force responsive sizing
        svg.removeAttribute('width');
        svg.removeAttribute('height');
        svg.style.width = '100%';
        svg.style.height = 'auto';
        svg.style.maxHeight = '720px';
        svg.style.display = 'block';

        wireUpInteractivity(svg, wrapper);
      })
      .catch(function (err) {
        console.error('[CD Map] Failed to load SVG:', err);
      });
  }

  // Calculate min price per category from the rendered ticket cards
  // (respects the active quantity filter)
  function calculatePriceRanges() {
    var ranges = {
      'floor':     { min: Infinity, count: 0 },
      '100-level': { min: Infinity, count: 0 },
      'salon':     { min: Infinity, count: 0 },
      '400-level': { min: Infinity, count: 0 }
    };
    visibleCards().forEach(function (c) {
      var cat = (c.getAttribute('data-category') || '').toLowerCase();
      var priceCents = cardPrice(c);
      if (!ranges[cat] || isNaN(priceCents)) return;
      if (priceCents < ranges[cat].min) ranges[cat].min = priceCents;
      ranges[cat].count++;
    });
    return ranges;
  }

  // Build a lookup: blockId (lowercased) → { price (cheapest cents), qty (total available), count }
  function buildBlockPriceIndex() {
    var index = {};
    visibleCards().forEach(function (c) {
      var block = (c.getAttribute('data-block') || '').toLowerCase();
      var price = cardPrice(c);
      if (!block || isNaN(price)) return;
      var maxQty = cardMaxQty(c);
      if (maxQty === 99) maxQty = 0; // unknown → don't fake a count
      if (!index[block]) {
        index[block] = { price: price, qty: maxQty, count: 1 };
      } else {
        if (price < index[block].price) index[block].price = price;
        index[block].qty += maxQty;
        index[block].count++;
      }
    });
    return index;
  }

  // Format a price in cents for display on the map pill (compact)
  function _formatMapPrice(cents) {
    if (window.CurrencyConverter && window.CurrencyConverter.isEurMode()) {
      return window.CurrencyConverter.convert(cents);
    }
    var dollars = Math.round(cents / 100);
    return '$' + dollars.toLocaleString('en-US');
  }

  // Render one compact price pill per SVG section:
  //  - exact pill (price + "N left") where a card matches the block id
  //  - category-fallback pill ("From $X") on every other purchasable section
  //  - sections whose whole category has zero availability are greyed out
  function renderPriceLabels(svg /*, ranges: unused, kept for compat */) {
    var svgNS = 'http://www.w3.org/2000/svg';

    var existing = svg.querySelector('#price-labels-group');
    if (existing) existing.parentNode.removeChild(existing);

    var group = document.createElementNS(svgNS, 'g');
    group.setAttribute('id', 'price-labels-group');
    group.setAttribute('pointer-events', 'none');
    svg.appendChild(group);

    var priceIndex = buildBlockPriceIndex();
    var ranges = calculatePriceRanges();
    var t = T();

    var sectionGroups = svg.querySelectorAll(
      'g[data-section^="floor_"], g[data-section^="lower_1"], g[data-section^="lower_2"], g[data-section^="upper_"]'
    );

    function makePill(cx, cy, lines) {
      // lines: [{text, size, weight, color}]
      var widest = 0;
      lines.forEach(function (l) {
        var w = l.text.length * (l.size * 0.72) + 20;
        if (w > widest) widest = w;
      });
      var pillWidth = Math.max(50, widest);
      var pillHeight = lines.length > 1 ? 32 : 20;

      var fo = document.createElementNS(svgNS, 'foreignObject');
      fo.setAttribute('x', cx - pillWidth / 2);
      fo.setAttribute('y', cy - pillHeight / 2);
      fo.setAttribute('width', pillWidth);
      fo.setAttribute('height', pillHeight);
      fo.setAttribute('pointer-events', 'none');
      fo.classList.add('cd-price-label-fo');

      var div = document.createElement('div');
      div.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
      div.className = 'cd-price-label cd-price-label--section';
      div.style.cssText =
        'display:flex;flex-direction:column;align-items:center;justify-content:center;' +
        'width:100%;height:100%;' +
        'background:rgba(17,24,39,0.92);' +
        'border:1.5px solid rgba(255,255,255,0.75);' +
        'border-radius:8px;' +
        'color:#fff;font-family:"Jost",system-ui,sans-serif;' +
        'box-shadow:0 2px 6px rgba(0,0,0,0.4);white-space:nowrap;' +
        'padding:2px 6px;gap:1px;';

      lines.forEach(function (l) {
        var span = document.createElement('span');
        span.style.cssText =
          'font-size:' + l.size + 'px;font-weight:' + l.weight + ';line-height:1.1;' +
          (l.color ? 'color:' + l.color + ';' : '') + 'letter-spacing:0.2px;';
        span.textContent = l.text;
        div.appendChild(span);
      });

      fo.appendChild(div);
      return fo;
    }

    Array.prototype.forEach.call(sectionGroups, function (g) {
      var sectionId = g.getAttribute('data-section') || '';
      var blockId = _blockFromSection(sectionId);
      var category = _categoryFromSection(sectionId);
      if (!blockId) return;

      var info = priceIndex[blockId.toLowerCase()];

      // Only sections with their OWN listing get a pill. Everything else is
      // greyed out — the old category-fallback pill painted "From $X" on
      // sections we had no tickets in (UX audit: buyer clicks 117, gets 102).
      if (!info) {
        g.classList.add('cd-svg-soldout');
        return;
      }
      g.classList.remove('cd-svg-soldout');

      var bbox;
      try { bbox = g.getBBox(); } catch (e) { return; }
      if (!bbox || bbox.width === 0 || bbox.height === 0) return;

      var cx = bbox.x + bbox.width / 2;

      // Exact listing(s) in this very section
      var priceText = _formatMapPrice(info.price);
      var qtyText = info.qty > 0 ? info.qty + t.left : '';
      var lines = [{ text: priceText, size: 14, weight: 800 }];
      if (qtyText) lines.push({ text: qtyText, size: 10, weight: 600, color: '#4ade80' });
      var pillHeight = lines.length > 1 ? 32 : 20;
      // Position at BOTTOM of section so block number stays visible above
      var cy = bbox.y + bbox.height - pillHeight / 2 - 2;
      var fo = makePill(cx, cy, lines);
      fo.setAttribute('data-block', blockId);
      group.appendChild(fo);
    });
  }

  // Hide labels when a filter is active (prevents visual clutter with tooltip)
  function setPriceLabelsVisible(svg, visible) {
    var group = svg.querySelector('#price-labels-group');
    if (group) group.style.opacity = visible ? '1' : '0';
  }

  function wireUpInteractivity(svg, wrapper) {
    // Find every section group we want to be interactive
    var groups = svg.querySelectorAll(
      'g[data-section^="floor_"], g[data-section^="lower_1"], g[data-section^="lower_2"], g[data-section^="upper_"]'
    );

    var tooltip = wrapper.parentNode.querySelector('.cd-map-tooltip');

    // Render price labels on the map (uniform pills + sold-out greying)
    renderPriceLabels(svg);
    window.__cdMapSvg = svg; // expose for filter toggle to hide/show labels

    console.log('[CD Map] Wired up ' + groups.length + ' SVG sections, ' + allCards().length + ' ticket cards.');

    // Detect currency from an existing price element on the page
    var _shopCurrencySymbol = '$';
    var _firstPriceEl = document.querySelector('.cd-map-tickets .ticket-variant-card .price-value');
    if (_firstPriceEl) {
      var _siblingCurrency = _firstPriceEl.parentNode && _firstPriceEl.parentNode.querySelector('.currency');
      if (_siblingCurrency && _siblingCurrency.textContent.trim()) {
        _shopCurrencySymbol = _siblingCurrency.textContent.trim();
      }
    }

    function formatPrice(cents) {
      // variant.price is stored as a string in cents (e.g. "85000" = $850.00)
      var num = parseFloat(cents);
      if (isNaN(num)) return null;

      // Support EUR conversion if CurrencyConverter is active
      if (window.CurrencyConverter && window.CurrencyConverter.isEurMode()) {
        return window.CurrencyConverter.convert(num);
      }

      var dollars = num / 100;
      // Strip .00 for whole-dollar amounts, otherwise show 2 decimals
      if (dollars % 1 === 0) return _shopCurrencySymbol + ' ' + dollars.toLocaleString('en-US');
      return _shopCurrencySymbol + ' ' + dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    // Find the price + listing count for a specific block id (e.g. "116")
    // or fall back to the cheapest price in the category tier
    function getPriceForSection(blockId, category) {
      var exactMatches = [];
      var categoryMatches = [];

      visibleCards().forEach(function (c) {
        var cardBlock = (c.getAttribute('data-block') || '').toLowerCase();
        var cardCat = (c.getAttribute('data-category') || '').toLowerCase();
        var price = c.getAttribute('data-price');
        if (!price) return;

        if (blockId && cardBlock === blockId.toLowerCase()) {
          exactMatches.push(parseFloat(price));
        } else if (cardCat === category) {
          categoryMatches.push(parseFloat(price));
        }
      });

      // Prefer exact block match if available
      if (exactMatches.length > 0) {
        var minExact = Math.min.apply(null, exactMatches);
        return { price: formatPrice(minExact), exact: true, count: exactMatches.length };
      }
      if (categoryMatches.length > 0) {
        var minCat = Math.min.apply(null, categoryMatches);
        return { price: formatPrice(minCat), exact: false, count: categoryMatches.length };
      }
      return null;
    }

    // --- bidirectional hover: map section → matching cards ---------------
    function setCardHover(blockId, category, on) {
      var cards = allCards();
      var blockHits = cards.filter(function (c) {
        return blockId && (c.getAttribute('data-block') || '').toLowerCase() === blockId.toLowerCase();
      });
      var targets = blockHits.length ? blockHits : cards.filter(function (c) {
        return (c.getAttribute('data-category') || '').toLowerCase() === category;
      });
      targets.forEach(function (c) {
        c.classList.toggle('cd-card-hover', !!on);
      });
      if (!on) {
        // safety: clear any strays
        cards.forEach(function (c) { c.classList.remove('cd-card-hover'); });
      }
    }

    Array.prototype.forEach.call(groups, function (g) {
      var sectionId = g.getAttribute('data-section') || '';
      var category = _categoryFromSection(sectionId);
      if (!category) return;

      g.style.cursor = 'pointer';
      g.setAttribute('data-cd-category', category);

      // Display label, e.g. "FLOOR A", "Section 113", "Section 213", "Section 414"
      var displayName = formatSectionName(sectionId);
      var categoryLabel = CATEGORY_LABELS[category] || category;
      var blockId = _blockFromSection(sectionId);

      g.addEventListener('mouseenter', function () {
        if (!g.classList.contains('cd-svg-soldout')) {
          setCardHover(blockId, category, true);
        }
        if (!tooltip) return;
        var t = T();
        var priceInfo = getPriceForSection(blockId, category);
        var priceHtml = '';
        if (priceInfo) {
          var fromLabel = priceInfo.exact ? '' : t.from + ' ';
          var countWord = priceInfo.count === 1 ? t.listing : t.listings;
          priceHtml = '<span class="cd-map-tooltip__price">' + fromLabel + priceInfo.price +
            ' · ' + priceInfo.count + ' ' + countWord + '</span>';
        } else {
          priceHtml = '<span class="cd-map-tooltip__price">' + t.soldout + '</span>';
        }
        tooltip.innerHTML =
          '<strong>' + displayName + '</strong>' +
          '<span class="cd-map-tooltip__cat">' + categoryLabel + '</span>' +
          priceHtml;
        tooltip.classList.add('is-visible');
      });

      g.addEventListener('mousemove', function (e) {
        if (!tooltip) return;
        var rect = wrapper.getBoundingClientRect();
        var x = e.clientX - rect.left + 14;
        var y = e.clientY - rect.top + 14;
        tooltip.style.left = x + 'px';
        tooltip.style.top = y + 'px';
      });

      g.addEventListener('mouseleave', function () {
        if (tooltip) tooltip.classList.remove('is-visible');
        setCardHover(blockId, category, false);
      });

      g.addEventListener('click', function (e) {
        e.stopPropagation();
        if (g.classList.contains('cd-svg-soldout')) return; // nothing to filter to
        applyFilter(category, blockId);
      });
    });

    // --- bidirectional hover: card → matching map section(s) -------------
    if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
      var sectionIndex = [];
      Array.prototype.forEach.call(groups, function (g) {
        var sid = g.getAttribute('data-section') || '';
        sectionIndex.push({ g: g, block: _blockFromSection(sid), cat: _categoryFromSection(sid) });
      });
      allCards().forEach(function (card) {
        if (card.dataset.cdHoverWired) return;
        card.dataset.cdHoverWired = '1';
        card.addEventListener('mouseenter', function () {
          var block = (card.getAttribute('data-block') || '').toLowerCase();
          var cat = (card.getAttribute('data-category') || '').toLowerCase();
          var blockHits = sectionIndex.filter(function (s) { return block && s.block === block; });
          var targets = blockHits.length ? blockHits : sectionIndex.filter(function (s) { return s.cat === cat; });
          targets.forEach(function (s) { s.g.classList.add('cd-svg-hot'); });
        });
        card.addEventListener('mouseleave', function () {
          sectionIndex.forEach(function (s) { s.g.classList.remove('cd-svg-hot'); });
        });
      });
    }

    // Show All button
    var showAllBtn = document.querySelector('.cd-map-show-all');
    if (showAllBtn) {
      showAllBtn.addEventListener('click', function () { applyFilter(null); });
    }
  }

  function formatSectionName(sectionId) {
    if (sectionId.indexOf('floor_') === 0) {
      return 'Floor ' + sectionId.replace('floor_', '').toUpperCase();
    }
    if (sectionId.indexOf('lower_') === 0) {
      return 'Section ' + sectionId.replace('lower_', '').toUpperCase();
    }
    if (sectionId.indexOf('upper_') === 0) {
      return 'Section ' + sectionId.replace('upper_', '').toUpperCase();
    }
    return sectionId;
  }

  var currentFilter = null;

  function applyFilter(category, blockId) {
    var cards = document.querySelectorAll('.ticket-variant-card');
    var showAllBtn = document.querySelector('.cd-map-show-all');

    // Build a unique filter key so toggling off works for both category and block clicks
    var filterKey = blockId ? ('block:' + blockId) : ('cat:' + category);

    // Toggle off if same filter clicked
    if (currentFilter === filterKey) {
      currentFilter = null;
      Array.prototype.forEach.call(cards, function (c) {
        c.classList.remove('cd-card-highlighted', 'cd-card-dimmed');
      });
      if (showAllBtn) showAllBtn.classList.remove('is-active');
      if (window.__cdMapSvg) setPriceLabelsVisible(window.__cdMapSvg, true);
      console.log('[CD Map] Filter cleared');
      return;
    }
    currentFilter = filterKey;

    if (!category && !blockId) {
      Array.prototype.forEach.call(cards, function (c) {
        c.classList.remove('cd-card-highlighted', 'cd-card-dimmed');
      });
      if (showAllBtn) showAllBtn.classList.remove('is-active');
      if (window.__cdMapSvg) setPriceLabelsVisible(window.__cdMapSvg, true);
      return;
    }

    // Hide price labels while a filter is active — the tooltip covers that info
    if (window.__cdMapSvg) setPriceLabelsVisible(window.__cdMapSvg, false);

    // Check if any cards have a matching block id (exact section match)
    var blockSpecificMatches = 0;
    if (blockId) {
      Array.prototype.forEach.call(cards, function (c) {
        var cardBlock = (c.getAttribute('data-block') || '').toLowerCase();
        if (cardBlock && cardBlock === blockId.toLowerCase()) {
          blockSpecificMatches++;
        }
      });
    }

    var firstMatch = null;
    var matchCount = 0;
    Array.prototype.forEach.call(cards, function (c) {
      var cardCat = (c.getAttribute('data-category') || '').toLowerCase();
      var cardBlock = (c.getAttribute('data-block') || '').toLowerCase();

      var isMatch = false;
      if (blockSpecificMatches > 0 && blockId) {
        // Prefer exact block match — only highlight cards for THIS specific section
        isMatch = cardBlock === blockId.toLowerCase();
      } else {
        // Fall back to category match (base tier)
        isMatch = cardCat === category;
      }

      if (isMatch) {
        c.classList.add('cd-card-highlighted');
        c.classList.remove('cd-card-dimmed');
        if (!firstMatch && !c.classList.contains('cd-qty-hidden')) firstMatch = c;
        matchCount++;
      } else {
        c.classList.add('cd-card-dimmed');
        c.classList.remove('cd-card-highlighted');
      }
    });

    var label = blockId
      ? ('block "' + blockId + '"' + (blockSpecificMatches > 0 ? ' (exact)' : ' → ' + category + ' tier'))
      : ('category "' + category + '"');
    console.log('[CD Map] Filter ' + label + ' → matched ' + matchCount + ' card(s)');

    // If cards are grouped in mobile accordions, open the groups that
    // contain a highlighted card so the matches are actually visible
    openGroupsForHighlighted();

    if (firstMatch) {
      firstMatch.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      // Pulse ring to draw the eye to the first matching card
      firstMatch.classList.remove('cd-card-pulse');
      // Force reflow so re-adding the class restarts the animation
      void firstMatch.offsetWidth;
      firstMatch.classList.add('cd-card-pulse');
      setTimeout(function () { firstMatch.classList.remove('cd-card-pulse'); }, 1400);
    }
    if (showAllBtn) showAllBtn.classList.add('is-active');
  }

  // Expose for the "Help me choose" chooser row + any external hook
  window.cdMapFilter = applyFilter;

  // Collapse the delivery + trust lines into ONE compact line beneath the
  // price block. Hides the two originals via CSS; this injects the combined
  // version so locale text stays consistent with the shop.
  function inlineTrustLine() {
    var htmlLang = document.documentElement.getAttribute('lang') || 'en';
    var text;
    if (htmlLang.indexOf('fr') === 0) {
      text = '📱 Transfert mobile 48h avant le concert · Garantie incluse';
    } else if (htmlLang.indexOf('es') === 0) {
      text = '📱 Transferencia móvil 48h antes del concierto · Garantía incluida';
    } else {
      text = '📱 Mobile transfer 48h before concert · 100% Guaranteed';
    }
    var cards = document.querySelectorAll('.cd-map-tickets .ticket-variant-card');
    Array.prototype.forEach.call(cards, function (card) {
      if (card.dataset.cdTrustInlined) return;
      var priceBlock = card.querySelector('.price-display-section');
      if (!priceBlock) return;
      var line = document.createElement('div');
      line.className = 'cd-one-liner';
      line.textContent = text;
      priceBlock.insertAdjacentElement('afterend', line);
      card.dataset.cdTrustInlined = '1';
    });
  }

  // Rewrite "Buy Now" CTA on Celine cards to a more emotional verb
  function rewriteCelineBuyNow() {
    var htmlLang = document.documentElement.getAttribute('lang') || 'en';
    var newLabel = 'Get These Tickets';
    if (htmlLang.indexOf('fr') === 0) newLabel = 'Obtenir ces billets';
    else if (htmlLang.indexOf('es') === 0) newLabel = 'Conseguir estas entradas';

    var btns = document.querySelectorAll('.cd-map-tickets .buy-now-btn');
    Array.prototype.forEach.call(btns, function (btn) {
      if (btn.dataset.cdRelabeled) return;
      // Replace only the text nodes (keep the SVG icon)
      Array.prototype.forEach.call(btn.childNodes, function (node) {
        if (node.nodeType === 3 && node.nodeValue.trim()) {
          node.nodeValue = ' ' + newLabel + ' ';
        }
      });
      btn.dataset.cdRelabeled = '1';
    });
  }

  // Wire the "Help me choose" chooser chips to the existing filter
  function wireChooserRow() {
    var chips = document.querySelectorAll('.cd-help-choose__btn');
    Array.prototype.forEach.call(chips, function (chip) {
      chip.addEventListener('click', function () {
        var cat = chip.getAttribute('data-cd-help');
        if (!cat) return;
        Array.prototype.forEach.call(chips, function (c) { c.classList.remove('is-active'); });
        chip.classList.add('is-active');
        applyFilter(cat, null);
      });
    });
  }

  // ================================================== PHASE 1 UX MODULE ==
  var UX = {
    qty: null,        // null | 2 | 3 | 4
    sort: 'asc',      // 'asc' | 'desc'
    grouped: false,
    panel: null,      // .cd-map-tickets
    grid: null,       // .ticket-cards-grid
    toolbar: null,
    bar: null,        // mobile sticky summary bar
    noMatchEl: null,
    panelVisible: false
  };

  function initPhase1UX() {
    var panel = document.querySelector('.cd-map-tickets');
    var grid = panel && panel.querySelector('.ticket-cards-grid');
    if (!panel || !grid || allCards().length === 0) return;
    UX.panel = panel;
    UX.grid = grid;

    buildToolbar();
    if (window.innerWidth < 768) buildGroups();
    applySort();
    updateGroupHeaders();
    openCheapestGroup();
    buildAllinFoot();
    buildMobileBar();
  }

  function buildToolbar() {
    var t = T();
    var bar = document.createElement('div');
    bar.className = 'cd-listing-toolbar';

    // All-in price line (point-of-price messaging)
    var allin = document.createElement('div');
    allin.className = 'cd-allin';
    allin.textContent = t.allin;
    bar.appendChild(allin);

    var row = document.createElement('div');
    row.className = 'cd-toolbar-row';

    // Quantity chips [Any][2][3][4+]
    var chips = document.createElement('div');
    chips.className = 'cd-qty-chips';
    chips.setAttribute('role', 'group');
    chips.setAttribute('aria-label', t.qtyLabel);
    var label = document.createElement('span');
    label.className = 'cd-qty-chips__label';
    label.textContent = t.qtyLabel;
    chips.appendChild(label);

    var maxAvail = 0;
    allCards().forEach(function (c) {
      var m = cardMaxQty(c);
      if (m !== 99 && m > maxAvail) maxAvail = m;
      if (m === 99) maxAvail = Math.max(maxAvail, 10);
    });

    [
      { val: null, text: t.any },
      { val: 2, text: '2' },
      { val: 3, text: '3' },
      { val: 4, text: '4+' }
    ].forEach(function (def) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cd-qty-chip';
      btn.textContent = def.text;
      if (def.val === null) btn.classList.add('is-active');
      if (def.val !== null && maxAvail < def.val) {
        btn.disabled = true; // no card can satisfy this quantity
      }
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        Array.prototype.forEach.call(chips.querySelectorAll('.cd-qty-chip'), function (b) {
          b.classList.remove('is-active');
        });
        btn.classList.add('is-active');
        applyQtyFilter(def.val);
      });
      chips.appendChild(btn);
    });
    row.appendChild(chips);

    // Sort control
    var sel = document.createElement('select');
    sel.className = 'cd-sort-select';
    sel.setAttribute('aria-label', t.sortAsc + ' / ' + t.sortDesc);
    var optAsc = document.createElement('option');
    optAsc.value = 'asc';
    optAsc.textContent = t.sortAsc;
    var optDesc = document.createElement('option');
    optDesc.value = 'desc';
    optDesc.textContent = t.sortDesc;
    sel.appendChild(optAsc);
    sel.appendChild(optDesc);
    sel.value = UX.sort;
    sel.addEventListener('change', function () {
      UX.sort = sel.value === 'desc' ? 'desc' : 'asc';
      applySort();
    });
    row.appendChild(sel);

    bar.appendChild(row);

    // Empty-state line for quantity filters with zero matches
    var noMatch = document.createElement('div');
    noMatch.className = 'cd-no-match';
    noMatch.textContent = t.noMatch;
    noMatch.hidden = true;
    UX.noMatchEl = noMatch;

    UX.grid.parentNode.insertBefore(bar, UX.grid);
    UX.grid.parentNode.insertBefore(noMatch, UX.grid);
    UX.toolbar = bar;
  }

  function buildAllinFoot() {
    var foot = document.createElement('p');
    foot.className = 'cd-allin-foot';
    foot.textContent = '✓ ' + T().allin;
    if (UX.grid.nextSibling) {
      UX.grid.parentNode.insertBefore(foot, UX.grid.nextSibling);
    } else {
      UX.grid.parentNode.appendChild(foot);
    }
  }

  // --- mobile accordions -------------------------------------------------
  function buildGroups() {
    var lang = _lang();
    var labels = GROUP_LABELS[lang] || GROUP_LABELS.en;
    var byCat = {};
    var order = [];

    allCards().forEach(function (c) {
      var cat = (c.getAttribute('data-category') || 'other').toLowerCase();
      if (!byCat[cat]) {
        byCat[cat] = [];
        order.push(cat);
      }
      byCat[cat].push(c);
    });
    // Stable, known-first ordering (final visual order is set by applySort)
    order.sort(function (a, b) {
      var ia = CAT_ORDER.indexOf(a); if (ia < 0) ia = 99;
      var ib = CAT_ORDER.indexOf(b); if (ib < 0) ib = 99;
      return ia - ib;
    });

    order.forEach(function (cat) {
      var wrap = document.createElement('div');
      wrap.className = 'cd-acc-group';
      wrap.setAttribute('data-cat', cat);

      var head = document.createElement('button');
      head.type = 'button';
      head.className = 'cd-acc-head';
      head.setAttribute('aria-expanded', 'false');
      var lbl = document.createElement('span');
      lbl.className = 'cd-acc-head__label';
      lbl.textContent = labels[cat] || (CATEGORY_LABELS[cat] || cat);
      var meta = document.createElement('span');
      meta.className = 'cd-acc-head__meta';
      var chev = document.createElement('span');
      chev.className = 'cd-acc-head__chev';
      chev.setAttribute('aria-hidden', 'true');
      head.appendChild(lbl);
      head.appendChild(meta);
      head.appendChild(chev);

      var body = document.createElement('div');
      body.className = 'cd-acc-body';

      byCat[cat].forEach(function (c) { body.appendChild(c); });

      head.addEventListener('click', function () {
        var open = wrap.classList.toggle('is-open');
        head.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      wrap.appendChild(head);
      wrap.appendChild(body);
      UX.grid.appendChild(wrap);
    });

    UX.grouped = true;
  }

  function groupMinMax(g) {
    var prices = [];
    Array.prototype.forEach.call(g.querySelectorAll('.ticket-variant-card'), function (c) {
      if (c.classList.contains('cd-qty-hidden')) return;
      var p = cardPrice(c);
      if (!isNaN(p)) prices.push(p);
    });
    if (!prices.length) return null;
    return { min: Math.min.apply(null, prices), max: Math.max.apply(null, prices), count: prices.length };
  }

  function updateGroupHeaders() {
    if (!UX.grouped) return;
    var t = T();
    Array.prototype.forEach.call(UX.grid.querySelectorAll('.cd-acc-group'), function (g) {
      var mm = groupMinMax(g);
      var meta = g.querySelector('.cd-acc-head__meta');
      if (!mm) {
        g.classList.add('cd-acc-hidden');
        return;
      }
      g.classList.remove('cd-acc-hidden');
      if (meta) {
        var word = mm.count === 1 ? t.listing : t.listings;
        meta.textContent = t.from.toLowerCase() + ' ' + _formatMapPrice(mm.min) + ' · ' + mm.count + ' ' + word;
      }
    });
    ensureOneGroupOpen();
  }

  function openCheapestGroup() {
    if (!UX.grouped) return;
    var groups = UX.grid.querySelectorAll('.cd-acc-group:not(.cd-acc-hidden)');
    if (!groups.length) return;
    var cheapest = null, cheapestMin = Infinity;
    Array.prototype.forEach.call(groups, function (g) {
      var mm = groupMinMax(g);
      if (mm && mm.min < cheapestMin) { cheapestMin = mm.min; cheapest = g; }
    });
    if (cheapest) {
      cheapest.classList.add('is-open');
      var h = cheapest.querySelector('.cd-acc-head');
      if (h) h.setAttribute('aria-expanded', 'true');
    }
  }

  function ensureOneGroupOpen() {
    if (!UX.grouped) return;
    var anyOpen = UX.grid.querySelector('.cd-acc-group.is-open:not(.cd-acc-hidden)');
    if (!anyOpen) openCheapestGroup();
  }

  function openGroupsForHighlighted() {
    if (!UX.grouped) return;
    var hi = document.querySelectorAll('.cd-map-tickets .ticket-variant-card.cd-card-highlighted');
    Array.prototype.forEach.call(hi, function (c) {
      var body = c.closest('.cd-acc-body');
      if (body && body.parentNode && !body.parentNode.classList.contains('is-open')) {
        body.parentNode.classList.add('is-open');
        var h = body.parentNode.querySelector('.cd-acc-head');
        if (h) h.setAttribute('aria-expanded', 'true');
      }
    });
  }

  // --- quantity filter ---------------------------------------------------
  function applyQtyFilter(n) {
    UX.qty = n;
    allCards().forEach(function (c) {
      var hide = !!(n && cardMaxQty(c) < n);
      c.classList.toggle('cd-qty-hidden', hide);
    });
    var anyVisible = visibleCards().length > 0;
    if (UX.noMatchEl) UX.noMatchEl.hidden = anyVisible;
    updateGroupHeaders();
    updateMobileBar();
    if (window.__cdMapSvg) renderPriceLabels(window.__cdMapSvg);
  }

  // --- sort --------------------------------------------------------------
  function applySort() {
    if (!UX.grid) return;
    var asc = UX.sort === 'asc';
    function cmp(a, b) {
      var pa = cardPrice(a) || 0;
      var pb = cardPrice(b) || 0;
      return asc ? pa - pb : pb - pa;
    }
    if (UX.grouped) {
      var groups = Array.prototype.slice.call(UX.grid.querySelectorAll('.cd-acc-group'));
      groups.forEach(function (g) {
        var body = g.querySelector('.cd-acc-body');
        if (!body) return;
        var cards = Array.prototype.slice.call(body.querySelectorAll('.ticket-variant-card')).sort(cmp);
        cards.forEach(function (c) { body.appendChild(c); });
      });
      groups.sort(function (ga, gb) {
        var a = groupMinMax(ga), b = groupMinMax(gb);
        var ka = a ? (asc ? a.min : -a.max) : Infinity;
        var kb = b ? (asc ? b.min : -b.max) : Infinity;
        return ka - kb;
      });
      groups.forEach(function (g) { UX.grid.appendChild(g); });
    } else {
      var flat = Array.prototype.slice.call(UX.grid.querySelectorAll('.ticket-variant-card')).sort(cmp);
      flat.forEach(function (c) { UX.grid.appendChild(c); });
    }
  }

  // --- mobile sticky summary bar ------------------------------------------
  function buildMobileBar() {
    var t = T();
    var bar = document.createElement('div');
    bar.className = 'cd-mob-bar';
    bar.setAttribute('aria-hidden', 'true');

    var info = document.createElement('div');
    info.className = 'cd-mob-bar__info';
    var price = document.createElement('span');
    price.className = 'cd-mob-bar__price';
    var sub = document.createElement('span');
    sub.className = 'cd-mob-bar__sub';
    info.appendChild(price);
    info.appendChild(sub);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cd-mob-bar__btn';
    btn.textContent = t.view;
    btn.addEventListener('click', function () {
      var target = UX.toolbar || UX.grid;
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    bar.appendChild(info);
    bar.appendChild(btn);
    document.body.appendChild(bar);
    UX.bar = bar;

    updateMobileBar();

    window.addEventListener('scroll', refreshBarVisibility, { passive: true });
    window.addEventListener('resize', refreshBarVisibility, { passive: true });
    refreshBarVisibility();
  }

  function refreshBarVisibility() {
    if (!UX.bar || !UX.grid) return;
    var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    // Visible while the user is above the listings (hero / map area);
    // hides as soon as the listings occupy the screen so it can never
    // cover a card's add-to-cart CTA.
    var gridTop = UX.grid.getBoundingClientRect().top;
    var beforeListings = gridTop > window.innerHeight * 0.55;
    var show = window.innerWidth < 768 && beforeListings && scrollY > 120 && visibleCards().length > 0;
    UX.bar.classList.toggle('is-visible', show);
    UX.bar.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  function updateMobileBar() {
    if (!UX.bar) return;
    var t = T();
    var prices = visibleCards().map(cardPrice).filter(function (p) { return !isNaN(p); });
    var priceEl = UX.bar.querySelector('.cd-mob-bar__price');
    var subEl = UX.bar.querySelector('.cd-mob-bar__sub');
    if (!prices.length) {
      priceEl.textContent = t.soldout;
      subEl.textContent = t.allinShort;
      return;
    }
    var min = Math.min.apply(null, prices);
    var word = prices.length === 1 ? t.listing : t.listings;
    priceEl.textContent = t.from + ' ' + _formatMapPrice(min);
    subEl.textContent = prices.length + ' ' + word + ' · ' + t.allinShort;
  }

  // ------------------------------------------------------------- bootstrap --
  // Run these after the ticket table has rendered (it's server-rendered Liquid,
  // so it's in the DOM at DOMContentLoaded — but fetch for the SVG is async)
  function runPostInit() {
    inlineTrustLine();
    rewriteCelineBuyNow();
    wireChooserRow();
    try {
      initPhase1UX();
    } catch (e) {
      console.error('[CD Map] Phase 1 UX init failed:', e);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runPostInit);
  } else {
    runPostInit();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Currency converter: re-render price labels when EUR rate is ready
  document.addEventListener('currency:ready', function () {
    var svg = window.__cdMapSvg;
    if (svg) renderPriceLabels(svg);
    updateGroupHeaders();
    updateMobileBar();
  });
})();
